import { useCallback, useEffect, useRef } from 'react';

import { UserService } from '@/services';
import { UserData } from '@/types';

import { useError } from './ErrorContext';
import { useUserState } from './UserStateContext';

export function useUsers() {
  const { state, dispatch } = useUserState();
  const { handleApiError } = useError();
  const hasAttemptedLoad = useRef(false);

  const loadUsers = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const users = await UserService.getAll();
      dispatch({ type: 'SET_USERS', payload: users });
    } catch (error) {
      const message = 'Failed to load users';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Load users', loadUsers);
    }
  }, [dispatch, handleApiError]);

  const getUserById = useCallback((id: string): UserData | undefined => {
    // Handle both string and number ID comparisons
    const found = state.users.find(user => 
      user.id.toString() === id || (typeof user.id === 'number' && user.id === Number(id))
    );
    return found;
  }, [state.users]);

  const updateUser = useCallback(async (id: string, updates: { user: Partial<UserData> }): Promise<UserData> => {
    try {
      const updatedUser = await UserService.update(id, updates);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
      return updatedUser;
    } catch (error) {
      const message = 'Failed to update user';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Update user');
      throw error;
    }
  }, [dispatch, handleApiError]);

  const deleteUser = useCallback(async (id: string): Promise<void> => {
    try {
      await UserService.delete(id);
      dispatch({ type: 'DELETE_USER', payload: id });
    } catch (error) {
      const message = 'Failed to delete user';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Delete user');
      throw error;
    }
  }, [dispatch, handleApiError]);

  const setCurrentUser = useCallback((user: UserData | null) => {
    dispatch({ type: 'SET_CURRENT_USER', payload: user });
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, [dispatch]);

  // Auto-load users on mount
  useEffect(() => {
    if (state.users.length === 0 && !state.loading && !hasAttemptedLoad.current) {
      hasAttemptedLoad.current = true;
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.users.length, state.loading]);

  return {
    users: state.users,
    currentUser: state.currentUser,
    loading: state.loading,
    error: state.error,
    loadUsers,
    getUserById,
    updateUser,
    deleteUser,
    setCurrentUser,
    clearError,
  };
}
